import { useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useVenue } from "../lib/venue";
import { getVenueEvents, joinMailingList } from "../lib/api";
import { pushCtaClick, pushFormInteraction } from "../lib/analytics";
import { resolveFooter } from "../lib/site-footer";

// Bespoke ARTIST site (websiteMode + ARTIST archetype), a PROMOTIONAL
// one-screen splash in the Keo mould: full-bleed atmospheric backdrop, thin
// nav (links · wordmark · socials), and dead-centre the latest release,
// artwork → title → ONE call to action. The scroll below the fold carries
// gigs (real platform events), the story and the mailing capture, but the
// first screen is pure promo. All driven by appConfig.artist + footer +
// branding, so it's white-label and live-previews in the admin builder.

const INK = "#0b0b0b";
const PAPER = "#f4f2ee";
const HAIR = "rgba(244,242,238,0.16)";
const NAV_LABEL = "text-[10px] font-semibold uppercase tracking-[0.25em] transition hover:opacity-60";

function useArtist() {
  const { data } = useVenue();
  const venue = data?.venue;
  const a = venue?.appConfig?.artist;
  // Rotating hero: one image from the pool per visit, a fresh random pick on
  // every load/refresh (memoised so it doesn't flicker on re-renders).
  const heroPool = (a?.heroImages ?? []).filter(Boolean);
  const heroKey = heroPool.join("|");
  const rotated = useMemo(
    () => (heroPool.length ? heroPool[Math.floor(Math.random() * heroPool.length)] : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [heroKey],
  );
  return {
    venue,
    name: venue?.appName || venue?.name || "",
    backdrop: rotated || a?.heroImageUrl || venue?.bannerUrl || venue?.imageUrl || null,
    wordmark: a?.wordmarkUrl || null,
    marquee: a?.marquee || null,
    release: a?.release || null,
    songs: (a?.songs ?? []).slice(0, 4), // capped: 4 songs, newest first
    gallery: a?.gallery ?? [],
    news: a?.news ?? [],
    shopUrl: venue?.appConfig?.shopUrl || null,
    bio: venue?.appDescription || "",
  };
}

// "28 Jun 2026" from an ISO-ish date string; the raw string if unparseable.
function newsDate(date?: string): string | null {
  if (!date) return null;
  const d = new Date(date);
  return Number.isNaN(d.getTime())
    ? date
    : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

export function ArtistSite() {
  const { venue, name, backdrop, wordmark, marquee, release, songs, gallery, news, shopUrl, bio } = useArtist();
  const venueId = venue?.id;
  const [email, setEmail] = useState("");
  const [signup, setSignup] = useState<"idle" | "sending" | "done" | "error">("idle");

  const eventsQ = useQuery({
    queryKey: ["events", venueId],
    queryFn: () => getVenueEvents(venueId!),
    enabled: !!venueId,
    staleTime: 60_000,
  });
  const gigs = (eventsQ.data ?? []).slice(0, 8);

  const footer = venue ? resolveFooter(venue, venue.appConfig?.footer, new Date()) : null;
  const socials = Object.entries(footer?.socials ?? {});

  const subscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!venueId || !email.trim() || signup === "sending") return;
    pushCtaClick("subscribe");
    // noValidate on the form means a malformed address reaches us instead of
    // being silently blocked by the browser's own type="email" validation,
    // otherwise this check (and the fail event below) would never run.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      pushFormInteraction("fail");
      setSignup("error");
      return;
    }
    setSignup("sending");
    try {
      await joinMailingList(venueId, email.trim(), "artist-site");
      pushFormInteraction("success");
      setSignup("done");
      setEmail("");
    } catch {
      pushFormInteraction("fail");
      setSignup("error");
    }
  };

  if (!venue) return null;

  return (
    <div style={{ backgroundColor: INK, color: PAPER }} className="min-h-[100dvh]">
      {/* ── THE PROMO SCREEN, backdrop + thin nav + centred release ──────── */}
      <section id="top" className="relative flex min-h-[100dvh] flex-col overflow-hidden">
        {backdrop && (
          <img
            src={backdrop}
            alt=""
            aria-hidden
            className="absolute inset-0 h-full w-full object-cover"
            style={{ filter: "grayscale(1) contrast(1.06) brightness(0.6)" }}
          />
        )}
        {/* Edge scrims keep the nav + centre legible over any image. */}
        <div className="absolute inset-0" aria-hidden style={{ background: "radial-gradient(ellipse at center, rgba(11,11,11,0) 30%, rgba(11,11,11,0.55) 100%)" }} />
        <div className="absolute inset-x-0 top-0 h-28" aria-hidden style={{ background: "linear-gradient(rgba(11,11,11,0.7), rgba(11,11,11,0))" }} />

        {/* Top bar: links left · socials right, the wordmark sits BELOW the
            bar (Clive: cleaner top line, band name lowered under it). */}
        <header className="relative z-10">
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-5">
            <nav className="flex items-center gap-5">
              {songs.length > 0 && <a href="#music" className={NAV_LABEL} style={{ color: PAPER }}>Music</a>}
              <a href="#gigs" className={NAV_LABEL} style={{ color: PAPER }}>Gigs</a>
              {news.length > 0 && (
                <Link to="/news" className={`${NAV_LABEL} hidden sm:inline`} style={{ color: PAPER }}>News</Link>
              )}
              {gallery.length > 0 && (
                <Link to="/gallery" className={`${NAV_LABEL} hidden sm:inline`} style={{ color: PAPER }}>Gallery</Link>
              )}
              {shopUrl && (
                <a href={shopUrl} target="_blank" rel="noopener noreferrer" className={NAV_LABEL} style={{ color: PAPER }}>Shop</a>
              )}
              <a href="#signup" className={NAV_LABEL} style={{ color: PAPER }}>Sign up</a>
            </nav>
            <div className="hidden items-center justify-end gap-5 sm:flex">
              {socials.slice(0, 6).map(([k, href]) => (
                <a key={k} href={href} target="_blank" rel="noopener noreferrer" className={NAV_LABEL} style={{ color: PAPER }}>
                  {k.slice(0, 2)}
                </a>
              ))}
            </div>
          </div>
          <a href="#top" className="mt-2 block px-6 text-center">
            {wordmark ? (
              <img src={wordmark} alt={name} className="mx-auto h-24 w-auto sm:h-32" />
            ) : (
              <span className="whitespace-nowrap font-extralight uppercase italic tracking-[0.35em]" style={{ fontSize: "clamp(2.2rem, 6vw, 4.5rem)" }}>
                {name}
              </span>
            )}
          </a>
        </header>

        {/* Hero centre: pure atmosphere, the release lives ONCE, in the Music
            section below (Clive: no doubling). A single quiet cue points down. */}
        <div className="relative z-10 flex flex-1 flex-col items-center justify-end px-6 pb-12 text-center">
          {release?.title && (
            <a
              href="#music"
              className="text-[10px] font-semibold uppercase tracking-[0.3em] opacity-70 transition hover:opacity-100"
              style={{ color: PAPER }}
            >
              {release.title}{release.label ? `, ${release.label}` : ""} ↓
            </a>
          )}
        </div>
      </section>

      {/* ── Marquee ───────────────────────────────────────────────────────── */}
      {marquee && (
        <div className="rb-marquee border-y py-2.5 text-[11px] font-semibold uppercase tracking-[0.3em]" style={{ borderColor: HAIR, color: PAPER }} aria-hidden>
          {[0, 1].map((t) => (
            <div key={t} className="rb-marquee__track">
              {Array.from({ length: 8 }).map((_, i) => (
                <span key={i} className="mx-8 inline-flex items-center gap-8">
                  <span>{marquee}</span>
                  <span className="opacity-40">✷</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      )}


      {/* ── Music: 4-song carousel (newest first; rotate via the builder) ─── */}
      {songs.length > 0 && (
        <section id="music" className="mx-auto max-w-6xl scroll-mt-16 px-6 py-24 sm:py-28">
          <div className="flex flex-wrap items-baseline justify-between gap-x-8 gap-y-2">
            <h2 className="text-[11px] font-semibold uppercase tracking-[0.3em] opacity-60">Music</h2>
            {(release?.links ?? []).length > 0 && (
              <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
                {release!.links!.map((l) => (
                  <a key={l.label} href={l.href} target="_blank" rel="noopener noreferrer" className="text-[10px] font-semibold uppercase tracking-[0.25em] opacity-70 transition hover:opacity-100" style={{ color: PAPER }}>
                    {l.label}
                  </a>
                ))}
              </div>
            )}
          </div>
          <div className="mt-8 flex snap-x snap-mandatory gap-6 overflow-x-auto pb-4 scrollbar-hide">
            {songs.map((t, i) => {
              const tile = (
                <>
                  {t.artworkUrl ? (
                    <img src={t.artworkUrl} alt={`${t.title} artwork`} className="aspect-square w-full object-cover" />
                  ) : (
                    <div className="flex aspect-square w-full items-center justify-center border" style={{ borderColor: HAIR }}>
                      <span className="px-4 text-center font-extralight italic" style={{ fontSize: "clamp(1.4rem, 3vw, 2.2rem)" }}>{t.title}</span>
                    </div>
                  )}
                  <p className="mt-4 text-sm font-semibold uppercase tracking-[0.3em]">{t.title}</p>
                  {i === 0 && <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.25em] opacity-60">Latest</p>}
                </>
              );
              return t.href ? (
                <a key={t.title} href={t.href} target="_blank" rel="noopener noreferrer" className="w-64 shrink-0 snap-start transition hover:opacity-80 sm:w-72">
                  {tile}
                </a>
              ) : (
                <div key={t.title} className="w-64 shrink-0 snap-start sm:w-72">{tile}</div>
              );
            })}
          </div>
        </section>
      )}

      {/* ── Gigs: REAL events from the platform ───────────────────────────── */}
      <section id="gigs" className="mx-auto max-w-6xl scroll-mt-16 px-6 py-24 sm:py-28" style={{ borderTop: `1px solid ${HAIR}` }}>
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.3em] opacity-60">Gigs</h2>
        {gigs.length === 0 ? (
          <p className="mt-6 font-extralight italic opacity-80" style={{ fontSize: "clamp(1.4rem, 3vw, 2rem)" }}>
            No shows announced, <a href="#signup" className="underline underline-offset-4">join the list</a> to hear first.
          </p>
        ) : (
          <ul className="mt-8">
            {gigs.map((g) => {
              const d = new Date(g.date);
              const rowCls = "flex flex-wrap items-baseline gap-x-6 gap-y-1 py-5 transition hover:opacity-70";
              const row = (
                <>
                    <span className="w-24 shrink-0 text-[13px] font-semibold uppercase tracking-widest tabular-nums opacity-70">
                      {d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-xl font-extralight italic sm:text-2xl">{g.title}</span>
                    <span className="text-[13px] uppercase tracking-widest opacity-70">
                      {[g.venue, g.city].filter(Boolean).join(" · ")}
                    </span>
                    <span className="border px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.25em]" style={{ borderColor: "rgba(244,242,238,0.4)" }}>
                      Tickets{g.externalTicketsUrl ? " ↗" : ""}
                    </span>
                </>
              );
              return (
                <li key={g.id} style={{ borderBottom: `1px solid ${HAIR}` }}>
                  {g.externalTicketsUrl ? (
                    // Sold elsewhere (their own site / the festival's), link straight out.
                    <a href={g.externalTicketsUrl} target="_blank" rel="noopener noreferrer" className={rowCls}>{row}</a>
                  ) : (
                    <Link to="/events/$id" params={{ id: g.id }} className={rowCls}>{row}</Link>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* ── News: latest posts tease → full posts at /news ────────────────── */}
      {news.length > 0 && (
        <section id="news" className="mx-auto max-w-6xl scroll-mt-16 px-6 py-24 sm:py-28" style={{ borderTop: `1px solid ${HAIR}` }}>
          <div className="flex flex-wrap items-baseline justify-between gap-x-8 gap-y-2">
            <h2 className="text-[11px] font-semibold uppercase tracking-[0.3em] opacity-60">News</h2>
            <Link to="/news" className="text-[10px] font-semibold uppercase tracking-[0.25em] opacity-70 transition hover:opacity-100" style={{ color: PAPER }}>
              All news →
            </Link>
          </div>
          <div className="mt-8 grid gap-8 sm:grid-cols-3">
            {news.slice(0, 3).map((p) => (
              <Link key={p.title} to="/news" className="group block">
                {p.imageUrl && (
                  <img src={p.imageUrl} alt="" className="aspect-[3/2] w-full object-cover transition group-hover:opacity-85" />
                )}
                {newsDate(p.date) && (
                  <p className="mt-4 text-[10px] font-semibold uppercase tracking-[0.25em] opacity-50">{newsDate(p.date)}</p>
                )}
                <h3 className="mt-2 font-extralight italic leading-snug" style={{ fontSize: "clamp(1.15rem, 2vw, 1.4rem)" }}>
                  {p.title}
                </h3>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ── About ─────────────────────────────────────────────────────────── */}
      {bio && (
        <section id="about" className="mx-auto max-w-6xl scroll-mt-16 px-6 py-24 sm:py-28" style={{ borderTop: `1px solid ${HAIR}` }}>
          <div className="grid items-center gap-10 md:grid-cols-2">
            {backdrop && (
              <img src={backdrop} alt={name} className="w-full object-cover" style={{ filter: "grayscale(1)", aspectRatio: "4/3", objectPosition: "top" }} />
            )}
            <div>
              <h2 className="text-[11px] font-semibold uppercase tracking-[0.3em] opacity-60">About</h2>
              <p className="mt-5 max-w-prose font-extralight leading-relaxed opacity-90" style={{ fontSize: "clamp(1.1rem, 2vw, 1.35rem)" }}>
                {bio}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* ── Sign up ───────────────────────────────────────────────────────── */}
      <section id="signup" className="scroll-mt-16 px-6 py-24 text-center sm:py-32" style={{ backgroundColor: PAPER, color: INK }}>
        <h2 className="font-extralight italic tracking-tight" style={{ fontSize: "clamp(2rem, 5vw, 3.6rem)" }}>
          {footer?.mailingListHeading || "Sign up"}
        </h2>
        {footer?.tagline && <p className="mt-2 text-sm opacity-70">{footer.tagline}</p>}
        {signup === "done" ? (
          <p className="mt-8 text-sm font-semibold uppercase tracking-[0.25em]">You're on the list.</p>
        ) : (
          <form onSubmit={subscribe} noValidate className="mx-auto mt-8 flex max-w-md border-2" style={{ borderColor: INK }}>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              aria-label="Email address"
              className="min-w-0 flex-1 bg-transparent px-4 py-3 text-sm outline-none placeholder:opacity-50"
              style={{ color: INK }}
            />
            <button type="submit" disabled={signup === "sending"} className="border-0 px-6 text-[11px] font-bold uppercase tracking-[0.25em] transition hover:opacity-80 disabled:opacity-50" style={{ backgroundColor: INK, color: PAPER }}>
              {signup === "sending" ? "…" : "Join"}
            </button>
          </form>
        )}
        {signup === "error" && <p className="mt-3 text-xs text-red-600">Couldn't subscribe, try again.</p>}
      </section>

      {/* ── Slim footer (white-label config, bespoke rendering) ───────────── */}
      <footer className="px-6 py-10" style={{ borderTop: `1px solid ${HAIR}` }}>
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-5">
          {socials.length > 0 && (
            <div className="flex flex-wrap items-center justify-center gap-x-7 gap-y-2">
              {socials.map(([k, href]) => (
                <a key={k} href={href} target="_blank" rel="noopener noreferrer" className="text-[11px] font-semibold uppercase tracking-[0.25em] transition hover:opacity-60" style={{ color: PAPER }}>
                  {k}
                </a>
              ))}
            </div>
          )}
          {(footer?.links ?? []).length > 0 && (
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-1 text-[11px] opacity-60">
              {footer!.links.map((l) => (
                <a key={l.label} href={l.href} className="transition hover:opacity-70">{l.label}</a>
              ))}
            </div>
          )}
          <p className="text-[11px] tracking-widest opacity-50">{footer?.copyright}</p>
        </div>
      </footer>
    </div>
  );
}

// ── Gallery: mosaic collage at /gallery ──────────────────────────────────────
// "Mosaic style, all random shapes and sizes", CSS-columns masonry keeps each
// photo's natural aspect, and a deterministic pseudo-random crop pattern mixes
// tall/square/wide/panoramic tiles so the wall feels collaged, not gridded.
// FULL COLOUR (Clive: "keep the colour ones"), click any photo to enlarge.
// Photos come from appConfig.artist.gallery (builder-editable).
const TILE_ASPECTS = [
  null, "1/1", "2/3", null, "16/9", "3/4", null, "4/3", "1/1", null, "9/16", null,
  "4/3", null, "1/1", "16/9", null, "3/4", null, "2/3", null, "4/3", "1/1", null,
] as const;

export function ArtistGalleryPage() {
  const { venue, name, wordmark, gallery } = useArtist();
  const [lightbox, setLightbox] = useState<number | null>(null);

  // Keyboard: Esc closes, arrows browse. Lock page scroll while open.
  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") setLightbox((i) => (i === null ? i : (i + 1) % gallery.length));
      if (e.key === "ArrowLeft") setLightbox((i) => (i === null ? i : (i - 1 + gallery.length) % gallery.length));
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [lightbox, gallery.length]);

  if (!venue) return null;
  return (
    <div style={{ backgroundColor: INK, color: PAPER }} className="min-h-[100dvh]">
      <header className="sticky top-0 z-40" style={{ backgroundColor: "rgba(11,11,11,0.9)", backdropFilter: "blur(8px)", borderBottom: `1px solid ${HAIR}` }}>
        <div className="mx-auto grid max-w-7xl grid-cols-[1fr_auto_1fr] items-center gap-4 px-6 py-4">
          <Link to="/" className={NAV_LABEL} style={{ color: PAPER }}>← Home</Link>
          <Link to="/" className="justify-self-center">
            {wordmark ? (
              <img src={wordmark} alt={name} className="h-9 w-auto" />
            ) : (
              <span className="whitespace-nowrap font-extralight uppercase italic tracking-[0.3em] text-xl">{name}</span>
            )}
          </Link>
          <span className="justify-self-end text-[10px] font-semibold uppercase tracking-[0.25em] opacity-60">Gallery</span>
        </div>
      </header>

      {gallery.length === 0 ? (
        <p className="px-6 py-24 text-center font-extralight italic opacity-70" style={{ fontSize: "clamp(1.3rem, 3vw, 1.8rem)" }}>
          Photos coming soon.
        </p>
      ) : (
        <div className="mx-auto max-w-7xl columns-2 gap-3 px-3 py-6 sm:columns-3 sm:gap-4 sm:px-6 lg:columns-4">
          {gallery.map((url, i) => {
            const aspect = TILE_ASPECTS[i % TILE_ASPECTS.length];
            return (
              <button
                key={url}
                type="button"
                onClick={() => setLightbox(i)}
                className="mb-3 block w-full break-inside-avoid cursor-zoom-in border-0 p-0 sm:mb-4"
                style={{ backgroundColor: "transparent" }}
                aria-label={`Enlarge photo ${i + 1}`}
              >
                <img
                  src={url}
                  alt={`${name}, photo ${i + 1}`}
                  loading={i < 8 ? "eager" : "lazy"}
                  className="w-full transition hover:opacity-90"
                  style={aspect ? { aspectRatio: aspect, objectFit: "cover" } : undefined}
                />
              </button>
            );
          })}
        </div>
      )}

      {/* Lightbox, click a photo to enlarge; backdrop/✕/Esc close, arrows browse. */}
      {lightbox !== null && gallery[lightbox] && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-10"
          style={{ backgroundColor: "rgba(11,11,11,0.94)" }}
          onClick={() => setLightbox(null)}
          role="dialog"
          aria-modal="true"
          aria-label={`Photo ${lightbox + 1} of ${gallery.length}`}
        >
          <img
            src={gallery[lightbox]}
            alt={`${name}, photo ${lightbox + 1}`}
            className="max-h-full max-w-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute right-4 top-4 border-0 p-2 text-2xl leading-none transition hover:opacity-60"
            style={{ backgroundColor: "transparent", color: PAPER }}
            aria-label="Close"
          >
            ✕
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightbox((lightbox - 1 + gallery.length) % gallery.length); }}
            className="absolute left-2 top-1/2 -translate-y-1/2 border-0 p-3 text-3xl font-extralight transition hover:opacity-60 sm:left-5"
            style={{ backgroundColor: "transparent", color: PAPER }}
            aria-label="Previous photo"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightbox((lightbox + 1) % gallery.length); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 border-0 p-3 text-3xl font-extralight transition hover:opacity-60 sm:right-5"
            style={{ backgroundColor: "transparent", color: PAPER }}
            aria-label="Next photo"
          >
            ›
          </button>
          <span className="absolute bottom-4 left-1/2 -translate-x-1/2 text-[11px] tracking-[0.25em] opacity-60" style={{ color: PAPER }}>
            {lightbox + 1} / {gallery.length}
          </span>
        </div>
      )}
    </div>
  );
}

// ── News: the full posts at /news ────────────────────────────────────────────
// Editorial reading page in the same ink-and-paper language: date, headline,
// image, body paragraphs (blank-line separated in config). Posts come from
// appConfig.artist.news (builder-editable, newest first).
export function ArtistNewsPage() {
  const { venue, name, wordmark, news } = useArtist();
  if (!venue) return null;
  return (
    <div style={{ backgroundColor: INK, color: PAPER }} className="min-h-[100dvh]">
      <header className="sticky top-0 z-40" style={{ backgroundColor: "rgba(11,11,11,0.9)", backdropFilter: "blur(8px)", borderBottom: `1px solid ${HAIR}` }}>
        <div className="mx-auto grid max-w-7xl grid-cols-[1fr_auto_1fr] items-center gap-4 px-6 py-4">
          <Link to="/" className={NAV_LABEL} style={{ color: PAPER }}>← Home</Link>
          <Link to="/" className="justify-self-center">
            {wordmark ? (
              <img src={wordmark} alt={name} className="h-9 w-auto" />
            ) : (
              <span className="whitespace-nowrap font-extralight uppercase italic tracking-[0.3em] text-xl">{name}</span>
            )}
          </Link>
          <span className="justify-self-end text-[10px] font-semibold uppercase tracking-[0.25em] opacity-60">News</span>
        </div>
      </header>

      {news.length === 0 ? (
        <p className="px-6 py-24 text-center font-extralight italic opacity-70" style={{ fontSize: "clamp(1.3rem, 3vw, 1.8rem)" }}>
          No news yet, check back soon.
        </p>
      ) : (
        <div className="mx-auto max-w-3xl px-6">
          {news.map((p, i) => (
            <article key={p.title} className="py-16 sm:py-20" style={i > 0 ? { borderTop: `1px solid ${HAIR}` } : undefined}>
              {newsDate(p.date) && (
                <p className="text-[10px] font-semibold uppercase tracking-[0.25em] opacity-50">{newsDate(p.date)}</p>
              )}
              <h1 className="mt-3 font-extralight italic leading-tight" style={{ fontSize: "clamp(1.7rem, 4vw, 2.6rem)" }}>
                {p.title}
              </h1>
              {p.imageUrl && <img src={p.imageUrl} alt="" className="mt-8 w-full object-cover" />}
              <div className="mt-8 space-y-5">
                {p.body.split(/\n\s*\n/).map((para, j) => (
                  <p key={j} className="font-extralight leading-relaxed opacity-90" style={{ fontSize: "clamp(1.05rem, 1.6vw, 1.2rem)" }}>
                    {para.trim()}
                  </p>
                ))}
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
